import { z } from 'zod';
import { planLimitsSchema, planUsageSchema } from './plan.js';
/**
 * An organisation's own settings.
 *
 * The sending identity here is what a new campaign *starts from*, never what
 * one sends with: a campaign freezes its own copy, so changing this cannot
 * change a campaign that has already gone out.
 */
export const organizationSettingsSchema = z.object({
    name: z.string().trim().min(1, 'an organisation needs a name').max(120),
    /** An IANA zone. Every timestamp is stored in UTC and read back in this. */
    timezone: z.string().trim().min(1).max(64),
    /**
     * Printed in the footer of every campaign.
     *
     * Commercial email is required to carry a physical address nearly
     * everywhere this will be sent from. Empty is allowed because it is the
     * honest starting state, and the footer omits the line rather than printing
     * a blank one — but the screen says what it is for.
     */
    postalAddress: z.string().trim().max(300),
    fromName: z.string().trim().max(120),
    fromEmail: z.union([
        z.string().trim().email('that is not an email address').max(200),
        z.literal(''),
    ]),
    replyTo: z.union([
        z.string().trim().email('that is not an email address').max(200),
        z.literal(''),
    ]),
});
/**
 * How connecting a Google account for sending ended.
 *
 * Shared because the two halves are a server redirect and a page reading the
 * url it landed on, with nothing in between to hold them together. They were
 * both plain strings: five values that happened to agree, one rename away from
 * the page silently rendering nothing — which is the exact defect the page was
 * changed to fix. A type makes that rename a build failure.
 *
 * `cancelled` is somebody changing their mind at Google's screen and is not an
 * error; the page is careful to draw it differently.
 */
export const GOOGLE_OAUTH_OUTCOMES = [
    'connected',
    'cancelled',
    'expired',
    'unavailable',
    'failed',
];
/**
 * What an organisation is allowed, and what it has used.
 *
 * Shown to the organisation itself, not only to operators. A limit somebody
 * only ever meets by being refused reads as a fault rather than as a plan.
 */
export const orgPlanLimitsSchema = z.object({
    plan: z.string(),
    limits: planLimitsSchema,
    usage: planUsageSchema,
});
//# sourceMappingURL=settings.js.map